import type { Appointment } from "@dentacore/shared-types";

/** Statuses that hold nothing; a family member in one is not drawn with the rest. */
const GONE = new Set(["CANCELLED", "NO_SHOW"]);

/**
 * **A family visit drawn as one booking** (FV-D2, FV-D15, STORY-197).
 *
 * The server keeps one booking per patient (FV-D1), back to back in one chair.
 * The day reads better as one card, so the first booking of each visit stands
 * for all of it: its end and its occupancy are the last one's, and `party`
 * carries the others in order — the card counts them, the panel lists them,
 * completing walks them. Every other booking passes through untouched.
 *
 * Done once, where a day is read, so every grid, the panel and the drag see
 * the same picture.
 */
export function groupVisits(rows: readonly Appointment[]): Appointment[] {
  const groups = new Map<string, Appointment[]>();
  for (const row of rows) {
    const key = row.visitGroupId ?? null;
    if (key === null || GONE.has(row.status)) continue;
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }

  const out: Appointment[] = [];
  for (const row of rows) {
    const key = row.visitGroupId ?? null;
    const group = key === null || GONE.has(row.status) ? undefined : groups.get(key);
    if (group === undefined || group.length < 2) {
      out.push(row);
      continue;
    }
    const ordered = [...group].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    const [head, ...rest] = ordered;
    if (head === undefined || head.id !== row.id) continue;
    const last = ordered[ordered.length - 1] ?? head;
    out.push({ ...head, endsAt: last.endsAt, occupiedUntil: last.occupiedUntil, party: rest });
  }
  return out;
}

/** Every booking a card stands for, the first included. */
export function visitMembers(appointment: Appointment): Appointment[] {
  return [appointment, ...(appointment.party ?? [])];
}

/** What a booking is for, every procedure named: "Check-up + Filling". */
export function procedureNames(appointment: Appointment): string | null {
  const first = appointment.procedureLabel ?? appointment.procedureSummary;
  const more = (appointment.extraProcedures ?? []).map((extra) => extra.label);
  const all = [...(first === null ? [] : [first]), ...more];
  return all.length === 0 ? null : all.join(" + ");
}

/** Every procedure a booking names, in order. */
export function procedureIds(appointment: Appointment): string[] {
  return [
    ...(appointment.procedureCodeId === null ? [] : [appointment.procedureCodeId]),
    ...(appointment.extraProcedures ?? []).map((extra) => extra.procedureCodeId),
  ];
}
